import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import {
	NAV_SETTINGS_PATH,
	SETTINGS_NAV_ITEM,
	entryPath,
	hasChildMenu,
	hiddenNavItems,
	isChildActive,
	isPathActive,
	navSections,
	visibleChildren,
} from '../../lib/nav-items'
import type { NavItem } from '../../lib/nav-items'
import { usePrefetchIntent } from '../../hooks/usePrefetchIntent'
import { useNavSettings } from '../../queries/navSettings'
import { AccountMenu } from './AccountMenu'

interface SidebarProps {
	onNavigate?: () => void
}

const FOCUS_RING =
	'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-chrome-foreground/80 focus-visible:ring-offset-1 focus-visible:ring-offset-chrome'

function topRowState(isCurrent: boolean, isOpenSection: boolean): string {
	if (isCurrent) return 'bg-chrome-foreground text-chrome font-medium'
	if (isOpenSection) return 'bg-transparent text-chrome-foreground font-medium hover:bg-chrome-foreground/8'
	return 'bg-transparent text-chrome-foreground/80 hover:text-chrome-foreground hover:bg-chrome-foreground/8'
}

function topRowClass(isCurrent: boolean, isOpenSection = false): string {
	return `flex items-center gap-2.5 px-3 py-2.5 rounded-lg border-none text-sm cursor-pointer text-left w-full transition-colors duration-150 ${FOCUS_RING} ${topRowState(isCurrent, isOpenSection)}`
}

function childRowClass(isActive: boolean): string {
	return `flex items-center gap-2 px-2.5 py-2 rounded-lg border-none text-xs cursor-pointer text-left w-full transition-colors duration-150 ${FOCUS_RING} ${
		isActive
			? 'bg-chrome-foreground text-chrome font-medium'
			: 'bg-transparent text-chrome-foreground/80 hover:text-chrome-foreground hover:bg-chrome-foreground/8'
	}`
}

export function Sidebar({ onNavigate }: SidebarProps) {
	const navigate = useNavigate()
	const { pathname } = useLocation()
	const { user, hasPermission, isLoading: isAuthLoading } = useAuth()
	const intent = usePrefetchIntent()
	const navSettings = useNavSettings(user?.id ?? '')
	const [isMoreToggled, setIsMoreToggled] = useState(false)

	const isSettingsVisible = hasPermission(SETTINGS_NAV_ITEM.permission!)
	const hiddenItems = hiddenNavItems(hasPermission, navSettings)
	const isOnMorePage = pathname === NAV_SETTINGS_PATH || hiddenItems.some((item) => isPathActive(pathname, item.path))
	const isMoreOpen = isMoreToggled || isOnMorePage

	function handleNav(path: string) {
		navigate(path)
		onNavigate?.()
	}

	if (isAuthLoading) {
		return (
			<nav className="flex flex-col gap-1 flex-1">
				{Array.from({ length: 5 }).map((_, i) => (
					<div key={i} className="h-9 rounded-lg bg-chrome-foreground/10 animate-pulse" />
				))}
			</nav>
		)
	}

	function renderItem(item: NavItem) {
		const isSectionActive = isPathActive(pathname, item.path)
		const isChildListVisible = isSectionActive && hasChildMenu(item, hasPermission)

		return (
			<div key={item.path}>
				<button
					{...intent(entryPath(item, hasPermission))}
					onClick={() => handleNav(entryPath(item, hasPermission))}
					aria-current={isSectionActive && !isChildListVisible ? 'page' : undefined}
					className={topRowClass(isSectionActive && !isChildListVisible, isSectionActive)}
				>
					<i className={`ti ${item.icon} text-lg`} aria-hidden="true" />
					{item.label}
				</button>

				{isChildListVisible && (
					<div className="ml-3 mt-0.5 mb-0.5 flex flex-col gap-0.5 border-l border-chrome-foreground/15 pl-3 animate-in slide-in-from-top-1 fade-in-0 duration-200">
						{visibleChildren(item, hasPermission).map((child) => {
							const isActive = isChildActive(pathname, item, child)
							return (
								<button
									key={child.path}
									{...intent(child.path)}
									onClick={() => handleNav(child.path)}
									aria-current={isActive ? 'page' : undefined}
									className={childRowClass(isActive)}
								>
									<i className={`ti ${child.icon} text-base`} aria-hidden="true" />
									{child.label}
								</button>
							)
						})}
					</div>
				)}
			</div>
		)
	}

	return (
		<nav className="flex flex-col flex-1 min-h-0" aria-label="Main navigation">
			<div className="flex flex-col flex-1 min-h-0 overflow-y-auto">
				{navSections(hasPermission, navSettings).map((section) => (
					<div key={section.label ?? 'main'} className="flex flex-col gap-0.5">
						{section.label && (
							<h2 className="px-3 pt-5 pb-1 text-[11px] font-medium uppercase tracking-wider text-chrome-foreground/80">{section.label}</h2>
						)}
						{section.items.map(renderItem)}
					</div>
				))}

				<div className="mt-4 flex flex-col gap-0.5">
					<button
						onClick={() => setIsMoreToggled(!isMoreOpen)}
						aria-expanded={isMoreOpen}
						aria-controls="sidebar-more"
						className={topRowClass(false)}
					>
						<i className="ti ti-dots text-lg" aria-hidden="true" />
						<span className="flex-1">More</span>
						<i
							className={`ti ti-chevron-down text-base transition-transform duration-150 ${isMoreOpen ? 'rotate-180' : ''}`}
							aria-hidden="true"
						/>
					</button>
					{isMoreOpen && (
						<div id="sidebar-more" className="flex flex-col gap-0.5">
							{hiddenItems.map(renderItem)}
							<button
								onClick={() => handleNav(NAV_SETTINGS_PATH)}
								aria-current={pathname === NAV_SETTINGS_PATH ? 'page' : undefined}
								className={topRowClass(pathname === NAV_SETTINGS_PATH)}
							>
								<i className="ti ti-adjustments-horizontal text-lg" aria-hidden="true" />
								Customize sidebar
							</button>
						</div>
					)}
				</div>
			</div>

			<div className="shrink-0">
				{isSettingsVisible && (
					<button
						onClick={() => handleNav(SETTINGS_NAV_ITEM.path)}
						aria-current={isPathActive(pathname, SETTINGS_NAV_ITEM.path) ? 'page' : undefined}
						className={`${topRowClass(isPathActive(pathname, SETTINGS_NAV_ITEM.path))} mt-2`}
					>
						<i className={`ti ${SETTINGS_NAV_ITEM.icon} text-lg`} aria-hidden="true" />
						{SETTINGS_NAV_ITEM.label}
					</button>
				)}

				<AccountMenu />
			</div>
		</nav>
	)
}
